/**
 * Boli Live - Checkout Page
 *
 * Multi-step checkout flow: Address → Payment → Confirmation
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  CreditCard,
  CheckCircle,
  Wallet,
  Banknote,
  Smartphone,
  Package,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useCartStore } from '../lib/stores/cart-store';
import { useAuthStore } from '../lib/stores/auth-store';
import { formatPKR, cn } from '../lib/utils';
import { API_BASE_URL } from '../lib/api-config';
import type { GeoLocation, Province, CartItem } from '@boli/types';

type CheckoutStep = 'address' | 'payment' | 'confirmation';
type PaymentMethod = 'wallet' | 'cod' | 'jazzcash' | 'easypaisa';

const PROVINCES: { value: Province; label: string }[] = [
  { value: 'punjab', label: 'Punjab' },
  { value: 'sindh', label: 'Sindh' },
  { value: 'kpk', label: 'Khyber Pakhtunkhwa' },
  { value: 'balochistan', label: 'Balochistan' },
  { value: 'islamabad', label: 'Islamabad Capital Territory' },
  { value: 'gilgit', label: 'Gilgit-Baltistan' },
  { value: 'ajk', label: 'Azad Jammu & Kashmir' },
];

const PAYMENT_METHODS: { value: PaymentMethod; label: string; icon: typeof Wallet; description: string }[] = [
  { value: 'wallet', label: 'Boli Wallet', icon: Wallet, description: 'Pay from your wallet balance' },
  { value: 'cod', label: 'Cash on Delivery', icon: Banknote, description: 'Pay when you receive (orders under PKR 5,000)' },
  { value: 'jazzcash', label: 'JazzCash', icon: Smartphone, description: 'Pay via JazzCash mobile wallet' },
  { value: 'easypaisa', label: 'Easypaisa', icon: Smartphone, description: 'Pay via Easypaisa mobile wallet' },
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const {
    cart,
    isLoading,
    isCheckingOut,
    checkoutError,
    lastOrder,
    fetchCart,
    checkout,
    clearCheckoutError,
    clearLastOrder,
  } = useCartStore();

  const [step, setStep] = useState<CheckoutStep>('address');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cod');
  const [notes, setNotes] = useState('');

  // Address form state
  const [address, setAddress] = useState({
    address: '',
    city: '',
    province: 'punjab' as Province,
    phone: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/profile');
    }
  }, [isAuthenticated, navigate]);

  // Fetch cart on mount
  useEffect(() => {
    fetchCart();
    clearLastOrder();
  }, [fetchCart, clearLastOrder]);

  // Redirect to confirmation if order was placed
  useEffect(() => {
    if (lastOrder) {
      setStep('confirmation');
    }
  }, [lastOrder]);

  const items = cart?.items || [];
  const isEmpty = items.length === 0;

  // Validate address form
  const validateAddress = (): boolean => {
    const errors: Record<string, string> = {};

    if (!address.address.trim()) {
      errors.address = 'Street address is required';
    }
    if (!address.city.trim()) {
      errors.city = 'City is required';
    }
    if (!address.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^03\d{9}$/.test(address.phone.replace(/\s/g, ''))) {
      errors.phone = 'Enter a valid Pakistani phone number (03XXXXXXXXX)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle next step
  const handleNextStep = () => {
    if (step === 'address') {
      if (validateAddress()) {
        setStep('payment');
      }
    } else if (step === 'payment') {
      handleCheckout();
    }
  };

  // Handle checkout
  const handleCheckout = async () => {
    clearCheckoutError();

    const shippingAddress: GeoLocation = {
      type: 'Point',
      coordinates: [0, 0], // Would be filled by geocoding in production
      city: address.city,
      province: address.province,
      address: `${address.address}, ${address.city}`,
    };

    const result = await checkout({
      shippingAddress,
      paymentMethod,
      notes: notes.trim() || undefined,
    });

    if (result.success) {
      setStep('confirmation');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Empty cart (and not on confirmation)
  if (isEmpty && step !== 'confirmation') {
    return (
      <div className="min-h-screen bg-secondary/30">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-lg p-12 text-center"
          >
            <Package className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Your cart is empty</h2>
            <p className="text-muted-foreground mb-6">
              Add some items to your cart before checking out.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-md font-semibold hover:bg-primary/90 transition-colors"
            >
              Start Shopping
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header with Steps */}
        <div className="mb-8">
          <button
            onClick={() => step === 'address' ? navigate('/cart') : setStep('address')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            disabled={step === 'confirmation'}
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 'address' ? 'Back to Cart' : 'Back'}
          </button>

          <h1 className="text-2xl font-bold text-foreground mb-6">Checkout</h1>

          {/* Step Indicator */}
          {step !== 'confirmation' && (
            <div className="flex items-center gap-2">
              <StepIndicator
                step={1}
                label="Address"
                isActive={step === 'address'}
                isComplete={step === 'payment'}
              />
              <div className="flex-1 h-0.5 bg-border" />
              <StepIndicator
                step={2}
                label="Payment"
                isActive={step === 'payment'}
                isComplete={false}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {step === 'address' && (
                <AddressStep
                  key="address"
                  address={address}
                  setAddress={setAddress}
                  formErrors={formErrors}
                  notes={notes}
                  setNotes={setNotes}
                />
              )}

              {step === 'payment' && (
                <PaymentStep
                  key="payment"
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  cartTotal={cart?.grandTotal || 0}
                  checkoutError={checkoutError}
                />
              )}

              {step === 'confirmation' && (
                <ConfirmationStep key="confirmation" order={lastOrder} />
              )}
            </AnimatePresence>
          </div>

          {/* Order Summary Sidebar */}
          {step !== 'confirmation' && (
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-card border border-border rounded-lg p-6 sticky top-24"
              >
                <h2 className="text-lg font-bold text-foreground mb-4">Order Summary</h2>

                {/* Items Preview */}
                <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                  {items.slice(0, 3).map((item: CartItem) => (
                    <div key={item.id} className="flex gap-3">
                      <div className="w-12 h-12 rounded bg-secondary overflow-hidden flex-shrink-0">
                        <img
                          src={item.product?.images?.[0] || '/placeholder-product.jpg'}
                          alt={item.product?.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground line-clamp-1">{item.product?.title}</p>
                        <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                  {items.length > 3 && (
                    <p className="text-xs text-muted-foreground">
                      +{items.length - 3} more items
                    </p>
                  )}
                </div>

                <div className="border-t border-border pt-4 space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span>{formatPKR(cart?.subtotal || 0)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Shipping</span>
                    <span>{formatPKR(cart?.shippingTotal || 0)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Tax (PST)</span>
                    <span>{formatPKR(cart?.pstTotal || 0)}</span>
                  </div>
                  <div className="flex justify-between text-foreground font-bold text-lg pt-2 border-t border-border">
                    <span>Total</span>
                    <span>{formatPKR(cart?.grandTotal || 0)}</span>
                  </div>
                </div>

                {/* Continue Button */}
                <button
                  onClick={handleNextStep}
                  disabled={isCheckingOut}
                  className={cn(
                    'w-full mt-6 py-3 rounded-md font-semibold flex items-center justify-center gap-2 transition-colors',
                    'bg-primary text-primary-foreground hover:bg-primary/90',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {isCheckingOut ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : step === 'address' ? (
                    <>
                      Continue to Payment
                      <ArrowRight className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Place Order
                      <CheckCircle className="w-4 h-4" />
                    </>
                  )}
                </button>
              </motion.div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Step Indicator Component
function StepIndicator({
  step,
  label,
  isActive,
  isComplete,
}: {
  step: number;
  label: string;
  isActive: boolean;
  isComplete: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
          isComplete
            ? 'bg-green-600 text-white'
            : isActive
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground'
        )}
      >
        {isComplete ? <CheckCircle className="w-4 h-4" /> : step}
      </div>
      <span
        className={cn(
          'text-sm font-medium',
          isActive ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        {label}
      </span>
    </div>
  );
}

// Address Step Component
function AddressStep({
  address,
  setAddress,
  formErrors,
  notes,
  setNotes,
}: {
  address: { address: string; city: string; province: Province; phone: string };
  setAddress: (addr: typeof address) => void;
  formErrors: Record<string, string>;
  notes: string;
  setNotes: (notes: string) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-card border border-border rounded-lg p-6"
    >
      <div className="flex items-center gap-3 mb-6">
        <MapPin className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-bold text-foreground">Shipping Address</h2>
      </div>

      <div className="space-y-4">
        {/* Street Address */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Street Address *
          </label>
          <input
            type="text"
            value={address.address}
            onChange={(e) => setAddress({ ...address, address: e.target.value })}
            placeholder="House #, Street, Area"
            className={cn(
              'w-full px-4 py-2 border rounded-md bg-background text-foreground',
              'focus:outline-none focus:ring-2 focus:ring-primary/50',
              formErrors.address ? 'border-red-500' : 'border-border'
            )}
          />
          {formErrors.address && (
            <p className="text-xs text-red-500 mt-1">{formErrors.address}</p>
          )}
        </div>

        {/* City and Province */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              City *
            </label>
            <input
              type="text"
              value={address.city}
              onChange={(e) => setAddress({ ...address, city: e.target.value })}
              placeholder="Karachi"
              className={cn(
                'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                'focus:outline-none focus:ring-2 focus:ring-primary/50',
                formErrors.city ? 'border-red-500' : 'border-border'
              )}
            />
            {formErrors.city && (
              <p className="text-xs text-red-500 mt-1">{formErrors.city}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Province *
            </label>
            <select
              value={address.province}
              onChange={(e) => setAddress({ ...address, province: e.target.value as Province })}
              className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {PROVINCES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Phone Number *
          </label>
          <input
            type="tel"
            value={address.phone}
            onChange={(e) => setAddress({ ...address, phone: e.target.value })}
            placeholder="03XX XXXXXXX"
            className={cn(
              'w-full px-4 py-2 border rounded-md bg-background text-foreground',
              'focus:outline-none focus:ring-2 focus:ring-primary/50',
              formErrors.phone ? 'border-red-500' : 'border-border'
            )}
          />
          {formErrors.phone && (
            <p className="text-xs text-red-500 mt-1">{formErrors.phone}</p>
          )}
        </div>

        {/* Order Notes */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Order Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Special delivery instructions..."
            rows={3}
            className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
          />
        </div>
      </div>
    </motion.div>
  );
}

// Payment Step Component
function PaymentStep({
  paymentMethod,
  setPaymentMethod,
  cartTotal,
  checkoutError,
}: {
  paymentMethod: PaymentMethod;
  setPaymentMethod: (method: PaymentMethod) => void;
  cartTotal: number;
  checkoutError: string | null;
}) {
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const codDisabled = cartTotal >= 500000; // COD limit: 5000 PKR (value stored in paisa)
  const walletInsufficient = walletBalance < cartTotal;

  // Fetch wallet balance
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const token = localStorage.getItem('boli_auth_token');
        const response = await fetch(`${API_BASE_URL}/api/wallet/balance`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setWalletBalance(data.wallet?.balance || 0);
        }
      } catch {
        // Ignore
      }
    };
    fetchBalance();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="bg-card border border-border rounded-lg p-6"
    >
      <div className="flex items-center gap-3 mb-6">
        <CreditCard className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-bold text-foreground">Payment Method</h2>
      </div>

      {checkoutError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Checkout Failed</p>
            <p className="text-sm text-red-600">{checkoutError}</p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {PAYMENT_METHODS.map((method) => {
          const Icon = method.icon;
          const isDisabled = method.value === 'cod' && codDisabled;
          const isWallet = method.value === 'wallet';

          return (
            <label
              key={method.value}
              className={cn(
                'flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-colors',
                paymentMethod === method.value
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50',
                isDisabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="radio"
                name="paymentMethod"
                value={method.value}
                checked={paymentMethod === method.value}
                onChange={() => !isDisabled && setPaymentMethod(method.value)}
                disabled={isDisabled}
                className="w-4 h-4 text-primary"
              />
              <Icon className="w-6 h-6 text-muted-foreground" />
              <div className="flex-1">
                <p className="font-medium text-foreground">{method.label}</p>
                <p className="text-sm text-muted-foreground">{method.description}</p>
                {isWallet && (
                  <div className="mt-1 flex items-center gap-2">
                    <span className={cn(
                      'text-sm font-medium',
                      walletInsufficient ? 'text-red-500' : 'text-green-600'
                    )}>
                      Balance: {formatPKR(walletBalance)}
                    </span>
                    {walletInsufficient && (
                      <Link
                        to="/wallet"
                        className="text-xs text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Top Up
                      </Link>
                    )}
                  </div>
                )}
                {isDisabled && (
                  <p className="text-xs text-red-500 mt-1">
                    Not available for orders above PKR 5,000
                  </p>
                )}
              </div>
            </label>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <p className="text-sm text-muted-foreground">
          <strong>Note:</strong> JazzCash and Easypaisa payments will redirect you to their payment gateway.
          Wallet payments are instant if you have sufficient balance.
        </p>
      </div>
    </motion.div>
  );
}

// Confirmation Step Component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ConfirmationStep({ order }: { order: any }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-card border border-border rounded-lg p-8 text-center"
    >
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>

      <h2 className="text-2xl font-bold text-foreground mb-2">Order Placed!</h2>
      <p className="text-muted-foreground mb-6">
        Thank you for your order. We'll send you a confirmation email shortly.
      </p>

      {order && (
        <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Order ID</p>
              <p className="font-mono text-foreground">{order.id?.slice(0, 8)}...</p>
            </div>
            <div>
              <p className="text-muted-foreground">Total Amount</p>
              <p className="font-bold text-foreground">{formatPKR(order.totalAmount || 0)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Payment Method</p>
              <p className="text-foreground capitalize">{order.paymentMethod?.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Status</p>
              <p className="text-foreground capitalize">{order.status?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/"
          className="px-6 py-3 bg-primary text-primary-foreground rounded-md font-semibold hover:bg-primary/90 transition-colors"
        >
          Continue Shopping
        </Link>
        <Link
          to="/orders"
          className="px-6 py-3 border border-border text-foreground rounded-md font-semibold hover:bg-muted transition-colors"
        >
          View Orders
        </Link>
      </div>
    </motion.div>
  );
}
